'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Trophy } from 'lucide-react';

// Mirrors the API shape returned by LeaderboardService.PlacementSummary.
type Placement = {
  axis: string;
  scope: string;
  period: string;
  periodStart: string;
  rank: number;
  score: number;
};

const AXIS_LABEL: Record<string, string> = {
  views: 'views',
  posts: 'posts',
  'comments-received': 'comments',
  'follows-gained': 'follows',
};

/**
 * Small inline pill rendered on /u/[stageName] when the subject is in the
 * top 10 of an all-time global board. W9.6 — only one pill at a time
 * (the subject's highest-ranked board). Weekly/scoped placements are not
 * surfaced since the 2026-07-08 leaderboard simplification.
 *
 * Fetches publicly via /api/leaderboards/by-person/{personId} — no auth.
 */
export default function LeaderboardPill({
  personId,
  className = '',
}: {
  personId: number | string;
  className?: string;
}) {
  const [best, setBest] = useState<Placement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/proxy/api/leaderboards/by-person/${encodeURIComponent(String(personId))}`,
          { cache: 'no-store' },
        );
        if (!res.ok) return;
        const json = (await res.json()) as { placements: Placement[] };
        const candidates = (json.placements ?? [])
          .filter((p) => p.period === 'all-time' && p.scope === 'global' && p.rank <= 10);
        candidates.sort((a, b) => a.rank - b.rank);
        if (!cancelled) setBest(candidates[0] ?? null);
      } catch {
        // Pill is decoration — silent fail leaves nothing rendered.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId]);

  if (!best) return null;

  const axis = AXIS_LABEL[best.axis] ?? best.axis;
  const label = `Top ${best.rank <= 10 ? '10' : '50'} in ${axis}`;
  const href = `/leaderboards?axis=${encodeURIComponent(best.axis)}`;

  return (
    <Link
      href={href}
      title={`Rank ${best.rank} on the ${axis} board`}
      className={
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ' +
        'bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/15 transition-colors ' +
        className
      }
    >
      <Trophy className="h-3.5 w-3.5" strokeWidth={1.75} />
      {label}
    </Link>
  );
}
